/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { Icon, Pressable, View } from '@components/atoms'
import { Tab as MoleculesTab } from '@components/molecules'
import { Draggable } from 'react-beautiful-dnd'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

import { useAppDispatch, routeActions, useAppSelector } from '@redux/index'
import { I18nType } from '@schemas'

interface PropsType {
	key: string
	id: string
	path: string
	index: number
	value: string
	label: string
	i18n: I18nType[]
}

/**
 * 열린 화면 탭 하나. 13px 500, 비활성 faint → 활성 ink + 1px 밑줄(Tabs indicator).
 * 드래그 정렬·닫기 동작은 그대로다.
 */
export const DraggableTab = (props: PropsType) => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()
	const userLanguage: string = useAppSelector(state => state.user.language)

	// variables
	const isHiddenTab: boolean = props.index === 0
	const i18n: I18nType[] = props.i18n ?? []
	const thisI18n =
		i18n.length > 0
			? i18n.filter(anI18n => anI18n.isoLanguageCode === userLanguage.toLowerCase())
			: []
	const title: string = thisI18n.length > 0 ? thisI18n[0].title : ''

	const a11yProps = React.useCallback((index: number) => {
		return {
			id: `full-width-tab-${index}`,
			'aria-controls': `full-width-tabpanel-${index}`,
		}
	}, [])

	const ChildTab = (
		<MoleculesTab
			key={props.id}
			label={props.label}
			value={`Tab ${props.index + 1}`}
			{...a11yProps(props.index)}
			className="boTab"
		/>
	)

	const onRemoveClick = React.useCallback(() => {
		reduxDispatch(routeActions.REMOVE_TAB_IDX(props.index))
	}, [props.index])

	return (
		<Draggable
			key={`${props.index}`}
			draggableId={`${props.index}`}
			index={props.index}
			disableInteractiveElementBlocking
		>
			{draggableProvided => {
				return (
					<View
						css={[
							style,
							isHiddenTab ? { width: '0.1px', padding: 0, overflow: 'hidden' } : {},
						]}
						ref={draggableProvided.innerRef}
						{...draggableProvided.draggableProps}
					>
						{React.cloneElement(ChildTab, {
							...props,
							...draggableProvided.dragHandleProps,
							label: title !== '' ? title : props.label,
						})}

						{!isHiddenTab && (
							<Pressable
								className="removeButton"
								onClick={onRemoveClick}
								aria-label="탭 닫기"
							>
								<Icon name="close" className="removeIcon" />
							</Pressable>
						)}
					</View>
				)
			}}
		</Draggable>
	)
}

const style = css({
	position: 'relative',
	display: 'flex',
	alignItems: 'stretch',
	height: '100%',

	'.boTab': {
		overflow: 'initial',
		height: '100%',
		minHeight: 0,
		width: 'fit-content',
		minWidth: 0,
		margin: 0,
		padding: '0 30px 0 14px',
		alignItems: 'center',
		justifyContent: 'center',
		fontFamily: boFont.ui,
		fontSize: 13,
		fontWeight: 500,
		letterSpacing: 0,
		textTransform: 'none',
		color: mono.faint,
		cursor: 'pointer',
		opacity: 1,
		transition: 'color 200ms ease-out',

		'&.Mui-selected': { color: mono.ink },
		'&:hover': { color: mono.ink },
	},

	'.removeButton': {
		position: 'absolute',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: 18,
		height: 18,
		top: 0,
		bottom: 0,
		right: 8,
		margin: 'auto',
		padding: 0,
		borderRadius: 2,
		opacity: 0,
		transition: 'opacity 200ms ease-out, background-color 200ms ease-out',

		'.removeIcon': { fontSize: 14, color: mono.dim },
		'&:hover': { background: mono.line },
		'&:hover .removeIcon': { color: mono.ink },
	},

	'&:hover .removeButton, .boTab.Mui-selected ~ .removeButton': { opacity: 1 },
})
